import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { DoExperience } from "@/components/DoExperience";
import { RelatedHubs } from "@/components/RelatedHubs";
import { SeoItemList } from "@/components/SeoItemList";
import { getSeoSectionItems } from "@/lib/seo.functions";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";


export const Route = createFileRoute("/do")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { page?: number; lane?: string; r?: string; city?: string; q?: string } => ({
    page: Number(search.page) > 0 ? Number(search.page) : 1,
    lane: typeof search.lane === "string" ? search.lane : undefined,
    r: typeof search.r === "string" ? search.r : undefined,
    city: typeof search.city === "string" ? search.city : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  loader: async () => {
    try {
      const { items } = await getSeoSectionItems({ data: { section: "do", limit: 30 } });
      return { items };
    } catch {
      return { items: [] };
    }
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const url = "https://whatfinland.com/do";
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "What to do in Finland — curated experiences",
      url,
      numberOfItems: items.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://whatfinland.com/place/${it.slug}`,
        name: it.name,
      })),
    };
    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://whatfinland.com/" },
        { "@type": "ListItem", position: 2, name: "Do", item: url },
      ],
    };
    return {
      meta: [
        { title: "What to Do in Helsinki — Experiences, Saunas & Islands" },
        { name: "description", content: "Handpicked activities, saunas, islands and design walks in Helsinki by what Finland Oy." },
        { property: "og:title", content: "What to Do — Helsinki" },
        { property: "og:description", content: "The walks, saunas, shops, and views worth your day in Helsinki." },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks("/do")],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(itemList) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  // `strict: false` so the /fi mirror route can reuse this component.
  const { items } = useLoaderData({ strict: false }) as any;
  return (
    <>
      <DoExperience />
      <RelatedHubs current="do" />
      <SeoItemList
        items={items}
        heading="All curated things to do in Finland"
        hrefBase="/place"
      />
    </>
  );
}
